import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { AdminRedactionTab } from '../components/AdminRedactionTab';
import { 
  LayoutDashboard, 
  Heart, 
  Users, 
  Handshake, 
  Mail, 
  LogOut,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  ChevronRight,
  Bell,
  Send,
  Loader2,
  Gift,
  Calendar,
  FileText,
  Sparkles,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { admin, logout, getAuthHeaders, loading } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [donations, setDonations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [reminderStats, setReminderStats] = useState(null);
  const [anniversaryStats, setAnniversaryStats] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingAnniversaries, setSendingAnniversaries] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [annualData, setAnnualData] = useState(null);
  const [annualYear, setAnnualYear] = useState(new Date().getFullYear());
  const [sendingAnnual, setSendingAnnual] = useState(false);
  const [resendDays, setResendDays] = useState(90);
  const [resendingReceipts, setResendingReceipts] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin');
      }
    } finally {
      setDataLoading(false);
    }
  }, [getAuthHeaders, logout, navigate]);

  const fetchTabData = useCallback(async () => {
    if (activeTab === 'redaction') return;
    setDataLoading(true);
    setFetchError(false);
    try {
      if (activeTab === 'annual') {
        const response = await axios.get(`${API}/admin/annual-receipts/${annualYear}`, { headers: getAuthHeaders() });
        setAnnualData(response.data);
      } else if (activeTab === 'reminders') {
        // Fetch reminder stats and anniversary stats
        const [reminderRes, anniversaryRes] = await Promise.all([
          axios.get(`${API}/reminders/stats`, { headers: getAuthHeaders() }),
          axios.get(`${API}/anniversaries/stats`, { headers: getAuthHeaders() })
        ]);
        setReminderStats(reminderRes.data);
        setAnniversaryStats(anniversaryRes.data);
      } else {
        const endpoint = `${API}/admin/${activeTab}`;
        const response = await axios.get(endpoint, { headers: getAuthHeaders() });
        
        switch (activeTab) {
          case 'donations':
            setDonations(response.data.donations);
            break;
          case 'volunteers':
            setVolunteers(response.data.volunteers);
            break;
          case 'partners':
            setPartners(response.data.partners);
            break;
          case 'contacts':
            setContacts(response.data.contacts);
            break;
          default:
            break;
        }
      }
    } catch (err) {
      setFetchError(true);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setDataLoading(false);
    }
  }, [activeTab, annualYear, getAuthHeaders]);

  const handleSendAnnualReceipts = async (resend = false) => {
    setSendingAnnual(true);
    try {
      const response = await axios.post(
        `${API}/admin/annual-receipts/${annualYear}/send${resend ? '?resend=true' : ''}`,
        {},
        { headers: getAuthHeaders() }
      );
      const { sent, failed, skipped } = response.data;
      if (failed > 0) {
        toast.warning(`${sent} envoyé(s), ${failed} échec(s), ${skipped} déjà envoyé(s). Vérifiez vos crédits SendGrid.`);
      } else {
        toast.success(`${sent} reçu(s) annuel(s) envoyé(s)${skipped > 0 ? `, ${skipped} déjà envoyé(s)` : ''}`);
      }
      fetchTabData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des reçus annuels');
    } finally {
      setSendingAnnual(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/reminders/process?language=fr`, {}, {
        headers: getAuthHeaders()
      });
      
      const { sent_successfully, total_found, failed } = response.data;
      
      if (total_found === 0) {
        toast.info('Aucun rappel à envoyer pour le moment');
      } else if (sent_successfully > 0) {
        toast.success(`${sent_successfully} rappel(s) envoyé(s) avec succès`);
        if (failed > 0) {
          toast.warning(`${failed} rappel(s) ont échoué`);
        }
      } else {
        toast.error('Erreur lors de l\'envoi des rappels');
      }
      
      // Refresh stats
      const statsResponse = await axios.get(`${API}/reminders/stats`, { headers: getAuthHeaders() });
      setReminderStats(statsResponse.data);
      
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des rappels');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendAnniversaries = async () => {
    setSendingAnniversaries(true);
    try {
      const response = await axios.post(`${API}/anniversaries/process?language=fr`, {}, {
        headers: getAuthHeaders()
      });
      
      const { sent_successfully, total_found, failed } = response.data;
      
      if (total_found === 0) {
        toast.info('Aucun anniversaire à célébrer aujourd\'hui');
      } else if (sent_successfully > 0) {
        toast.success(`${sent_successfully} courriel(s) d'anniversaire envoyé(s)`);
        if (failed > 0) {
          toast.warning(`${failed} envoi(s) ont échoué`);
        }
      } else {
        toast.error('Erreur lors de l\'envoi des courriels');
      }
      
      // Refresh stats
      const statsResponse = await axios.get(`${API}/anniversaries/stats`, { headers: getAuthHeaders() });
      setAnniversaryStats(statsResponse.data);
      
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des courriels d\'anniversaire');
    } finally {
      setSendingAnniversaries(false);
    }
  };

  useEffect(() => {
    if (!loading && !admin) {
      navigate('/admin');
    }
  }, [admin, loading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchDashboardData();
    }
  }, [admin, fetchDashboardData]);

  useEffect(() => {
    if (admin && activeTab !== 'dashboard') {
      fetchTabData();
    }
  }, [activeTab, admin, fetchTabData]);

  const updateStatus = async (type, id, status) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}/status?status=${status}`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Statut mis à jour');
      fetchTabData();
      fetchDashboardData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-CA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800'
    };
    const labels = {
      pending: 'En attente',
      contacted: 'Contacté',
      resolved: 'Résolu',
      completed: 'Complété'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleResendReceipts = async () => {
    const periodLabel = resendDays === 0 ? 'TOUS les dons' : `les dons des ${resendDays} derniers jours`;
    if (!window.confirm(`Renvoyer par courriel les reçus fiscaux pour ${periodLabel} ? Chaque donateur admissible recevra son reçu PDF.`)) {
      return;
    }
    setResendingReceipts(true);
    try {
      const response = await axios.post(
        `${API}/admin/receipts/resend-bulk?days=${resendDays}`,
        {},
        { headers: getAuthHeaders() }
      );
      const { processed, sent, failed } = response.data;
      if (failed > 0) {
        toast.warning(`${sent}/${processed} reçu(s) envoyé(s), ${failed} échec(s)`);
      } else {
        toast.success(`${sent} reçu(s) fiscal(aux) renvoyé(s) par courriel`);
      }
    } catch (error) {
      toast.error('Erreur lors du renvoi des reçus');
    } finally {
      setResendingReceipts(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/donations/export?year=${annualYear}`, {
        responseType: 'blob',
        headers: getAuthHeaders()
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dons_FondationEnat_${annualYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export CSV ${annualYear} téléchargé`);
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-[#546A76]">Chargement...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'donations', label: 'Dons', icon: Heart },
    { id: 'volunteers', label: 'Bénévoles', icon: Users },
    { id: 'partners', label: 'Partenaires', icon: Handshake },
    { id: 'contacts', label: 'Messages', icon: Mail },
    { id: 'reminders', label: 'Rappels', icon: Bell },
    { id: 'annual', label: 'Reçus annuels', icon: FileText },
    { id: 'redaction', label: 'Rédaction IA', icon: Sparkles },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#1A252C] text-white p-6">
        <div className="mb-8">
          <h1 className="font-serif text-xl font-bold">Fondation Enat</h1>
          <p className="text-sm text-gray-400">Administration</p>
        </div>

        <nav className="space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#4A708B] text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="text-sm text-gray-400 mb-4">
            Connecté: {admin?.email}
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="logout-btn"
            className="w-full border-gray-600 text-gray-400 hover:bg-white/10 hover:text-white rounded-xl"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Dashboard View */}
        {activeTab === 'dashboard' && dashboardData && (
          <div>
            <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
              Tableau de bord
            </h2>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {dashboardData.stats.total_amount.toFixed(2)}$
                </p>
                <p className="text-sm text-[#546A76]">Total des dons</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#4A708B]/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-[#4A708B]" />
                  </div>
                  <span className="text-sm text-[#546A76]">{dashboardData.stats.total_donations} dons</span>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {dashboardData.stats.total_volunteers}
                </p>
                <p className="text-sm text-[#546A76]">Bénévoles inscrits</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Handshake className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {dashboardData.stats.total_partners}
                </p>
                <p className="text-sm text-[#546A76]">Demandes de partenariat</p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {dashboardData.stats.total_contacts}
                </p>
                <p className="text-sm text-[#546A76]">Messages reçus</p>
              </div>
            </div>

            {/* Pending Items */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">En attente</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-yellow-700">Bénévoles: {dashboardData.stats.pending_volunteers}</span>
                  <span className="text-yellow-700">Partenaires: {dashboardData.stats.pending_partners}</span>
                  <span className="text-yellow-700">Messages: {dashboardData.stats.pending_contacts}</span>
                </div>
              </div>
            </div>

            {/* Recent Donations */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-lg font-semibold text-[#1A252C]">
                  Derniers dons
                </h3>
                <button
                  onClick={() => setActiveTab('donations')}
                  className="text-sm text-[#4A708B] hover:underline flex items-center"
                >
                  Voir tout <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-4">
                {dashboardData.recent_donations.map((donation) => (
                  <div key={donation.id || donation.payment_id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0] last:border-0">
                    <div>
                      <p className="font-medium text-[#1A252C]">
                        {donation.is_anonymous ? 'Anonyme' : donation.donor_name}
                      </p>
                      <p className="text-sm text-[#546A76]">{formatDate(donation.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#D4AF37]">{donation.amount}$ CAD</p>
                      <p className="text-xs text-[#546A76]">
                        {donation.donation_type === 'monthly' ? 'Mensuel' : 'Unique'}
                      </p>
                    </div>
                  </div>
                ))}
                {dashboardData.recent_donations.length === 0 && (
                  <p className="text-center text-[#546A76] py-4">Aucun don pour le moment</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Donations List */}
        {activeTab === 'donations' && (
          <div>
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <h2 className="font-serif text-2xl font-bold text-[#1A252C]">
                Dons ({donations.length})
              </h2>
              <div className="flex items-center gap-3">
                <select
                  value={resendDays}
                  onChange={(e) => setResendDays(parseInt(e.target.value, 10))}
                  data-testid="resend-receipts-period"
                  className="rounded-full border border-[#E2E8F0] bg-white px-4 py-2 text-sm text-[#1A252C] focus:outline-none focus:border-[#4A708B]"
                >
                  <option value={30}>30 derniers jours</option>
                  <option value={60}>60 derniers jours</option>
                  <option value={90}>90 derniers jours</option>
                  <option value={0}>Tous les dons</option>
                </select>
                <Button
                  onClick={handleResendReceipts}
                  disabled={resendingReceipts}
                  className="gold-btn rounded-full"
                  data-testid="resend-receipts-btn"
                >
                  {resendingReceipts ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Renvoyer les reçus
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#FAFAFA]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Donateur</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Courriel</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Montant</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Type</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((donation) => (
                    <tr key={donation.id || donation.payment_id} className="border-t border-[#E2E8F0]">
                      <td className="px-6 py-4">{donation.is_anonymous ? 'Anonyme' : donation.donor_name}</td>
                      <td className="px-6 py-4 text-[#546A76]">{donation.email}</td>
                      <td className="px-6 py-4 font-bold text-[#D4AF37]">{donation.amount}$ CAD</td>
                      <td className="px-6 py-4">{donation.donation_type === 'monthly' ? 'Mensuel' : 'Unique'}</td>
                      <td className="px-6 py-4 text-sm text-[#546A76]">{formatDate(donation.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {donations.length === 0 && (
                <p className="text-center text-[#546A76] py-8">Aucun don pour le moment</p>
              )}
            </div>
          </div>
        )}

        {/* Volunteers List */}
        {activeTab === 'volunteers' && (
          <div>
            <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
              Bénévoles ({volunteers.length})
            </h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#FAFAFA]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Nom</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Courriel</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Téléphone</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Ville</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Statut</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {volunteers.map((volunteer) => (
                    <tr key={volunteer.id} className="border-t border-[#E2E8F0]">
                      <td className="px-6 py-4">{volunteer.first_name} {volunteer.last_name}</td>
                      <td className="px-6 py-4 text-[#546A76]">{volunteer.email}</td>
                      <td className="px-6 py-4">{volunteer.phone}</td>
                      <td className="px-6 py-4">{volunteer.city}</td>
                      <td className="px-6 py-4">{getStatusBadge(volunteer.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateStatus('volunteers', volunteer.id, 'contacted')}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Marquer comme contacté"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updateStatus('volunteers', volunteer.id, 'resolved')}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Marquer comme résolu"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {volunteers.length === 0 && (
                <p className="text-center text-[#546A76] py-8">Aucun bénévole inscrit</p>
              )}
            </div>
          </div>
        )}

        {/* Partners List */}
        {activeTab === 'partners' && (
          <div>
            <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
              Demandes de partenariat ({partners.length})
            </h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#FAFAFA]">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Organisation</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Courriel</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Type</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Statut</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-[#546A76]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr key={partner.id} className="border-t border-[#E2E8F0]">
                      <td className="px-6 py-4">{partner.organization_name}</td>
                      <td className="px-6 py-4">{partner.contact_name}</td>
                      <td className="px-6 py-4 text-[#546A76]">{partner.email}</td>
                      <td className="px-6 py-4">{partner.partnership_type}</td>
                      <td className="px-6 py-4">{getStatusBadge(partner.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => updateStatus('partners', partner.id, 'contacted')}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                            title="Marquer comme contacté"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => updateStatus('partners', partner.id, 'resolved')}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Marquer comme résolu"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {partners.length === 0 && (
                <p className="text-center text-[#546A76] py-8">Aucune demande de partenariat</p>
              )}
            </div>
          </div>
        )}

        {/* Contacts List */}
        {activeTab === 'contacts' && (
          <div>
            <h2 className="font-serif text-2xl font-bold text-[#1A252C] mb-8">
              Messages de contact ({contacts.length})
            </h2>
            <div className="space-y-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-[#1A252C]">{contact.subject}</h3>
                      <p className="text-sm text-[#546A76]">
                        De: {contact.name} ({contact.email})
                      </p>
                      <p className="text-xs text-[#546A76]">{formatDate(contact.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(contact.status)}
                      <button
                        onClick={() => updateStatus('contacts', contact.id, 'contacted')}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Marquer comme contacté"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateStatus('contacts', contact.id, 'resolved')}
                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                        title="Marquer comme résolu"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-[#546A76] whitespace-pre-wrap">{contact.message}</p>
                  {contact.phone && (
                    <p className="text-sm text-[#546A76] mt-2">Téléphone: {contact.phone}</p>
                  )}
                </div>
              ))}
              {contacts.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center text-[#546A76]">
                  Aucun message reçu
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reminders Tab */}
        {/* Annual Receipts View */}
        {activeTab === 'annual' && (
          <div data-testid="admin-annual-receipts">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl font-bold text-[#1A252C]">
                Reçus fiscaux annuels
              </h2>
              <div className="flex items-center gap-3">
                <div className="flex rounded-full bg-white p-1 border border-[#E2E8F0]">
                  {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                    <button
                      key={`year-${y}`}
                      onClick={() => setAnnualYear(y)}
                      data-testid={`annual-year-${y}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        annualYear === y ? 'bg-[#4A708B] text-white' : 'text-[#546A76] hover:text-[#1A252C]'
                      }`}
                    >
                      {y}
                    </button>
                  ))}
                </div>
                <Button
                  onClick={handleExportCsv}
                  variant="outline"
                  className="rounded-full border-[#4A708B] text-[#4A708B] hover:bg-[#4A708B]/10"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporter CSV
                </Button>
                <Button
                  onClick={() => handleSendAnnualReceipts(false)}
                  disabled={sendingAnnual || !annualData?.eligible_count}
                  className="gold-btn rounded-full"
                  data-testid="send-annual-receipts-btn"
                >
                  {sendingAnnual ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer par courriel
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#4A708B]/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#4A708B]" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]" data-testid="annual-eligible-count">
                  {annualData?.eligible_count || 0}
                </p>
                <p className="text-sm text-[#546A76]">Donateurs admissibles (25$+)</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {(annualData?.total_amount || 0).toFixed(2)}$
                </p>
                <p className="text-sm text-[#546A76]">Total des dons {annualYear}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {annualData?.sent_count || 0}
                </p>
                <p className="text-sm text-[#546A76]">Reçus annuels envoyés</p>
              </div>
            </div>

            {/* Donors Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {annualData?.donors?.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#546A76]">Donateur</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-[#546A76]">Courriel</th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-[#546A76]">Dons</th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-[#546A76]">Total</th>
                      <th className="text-center px-6 py-4 text-sm font-semibold text-[#546A76]">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {annualData.donors.map((d, idx) => (
                      <tr key={`annual-donor-${idx}`} className="border-b border-[#E2E8F0] last:border-0">
                        <td className="px-6 py-4 text-[#1A252C] font-medium">{d.donor_name}</td>
                        <td className="px-6 py-4 text-[#546A76] text-sm">{d.email}</td>
                        <td className="px-6 py-4 text-right text-[#546A76]">{d.count}</td>
                        <td className="px-6 py-4 text-right font-semibold text-[#1A252C]">{d.total.toFixed(2)}$</td>
                        <td className="px-6 py-4 text-center">
                          {!d.eligible ? (
                            <span className="inline-block px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-500">Non admissible</span>
                          ) : d.sent ? (
                            <span className="inline-block px-3 py-1 rounded-full text-xs bg-green-100 text-green-700">Envoyé</span>
                          ) : (
                            <span className="inline-block px-3 py-1 rounded-full text-xs bg-amber-100 text-amber-700">À envoyer</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-[#546A76]">
                  Aucun don complété pour l'année {annualYear}
                </div>
              )}
            </div>

            {/* Information Box */}
            <div className="bg-white rounded-2xl p-6 shadow-sm mt-8">
              <h3 className="font-serif text-lg font-semibold text-[#1A252C] mb-4">
                À propos des reçus annuels cumulatifs
              </h3>
              <div className="space-y-3 text-[#546A76] text-sm">
                <p>
                  Le reçu annuel regroupe <strong className="text-[#1A252C]">tous les dons complétés</strong> (uniques + mensuels) 
                  d'un donateur pour l'année civile, dès que le total atteint <strong className="text-[#1A252C]">25$</strong>.
                </p>
                <p>
                  Cliquez sur <strong>« Envoyer par courriel »</strong> pour envoyer les reçus PDF à tous les donateurs admissibles. 
                  Les donateurs ayant déjà reçu leur reçu annuel sont automatiquement ignorés. 
                  Les donateurs avec un compte peuvent aussi télécharger leur reçu annuel depuis leur Espace donateur.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'redaction' && (
          <AdminRedactionTab getAuthHeaders={getAuthHeaders} />
        )}

        {activeTab === 'reminders' && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-2xl font-bold text-[#1A252C]">
                Rappels de paiement
              </h2>
              <Button
                onClick={handleSendReminders}
                disabled={sendingReminders}
                className="gold-btn rounded-full"
                data-testid="send-reminders-btn"
              >
                {sendingReminders ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer les rappels
                  </>
                )}
              </Button>
            </div>

            {/* Reminder Stats Cards */}
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#4A708B]/10 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#4A708B]" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {reminderStats?.total_active_subscriptions || 0}
                </p>
                <p className="text-sm text-[#546A76]">Abonnements actifs</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {reminderStats?.upcoming_payments_7_days || 0}
                </p>
                <p className="text-sm text-[#546A76]">Paiements dans 7 jours</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-[#1A252C]">
                  {reminderStats?.reminders_sent_today || 0}
                </p>
                <p className="text-sm text-[#546A76]">Rappels envoyés aujourd'hui</p>
              </div>
            </div>

            {/* Information Box */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-lg font-semibold text-[#1A252C] mb-4">
                À propos des rappels automatiques
              </h3>
              <div className="space-y-4 text-[#546A76]">
                <p>
                  Les rappels de paiement sont envoyés automatiquement aux donateurs récurrents 
                  <strong className="text-[#1A252C]"> 3 jours avant</strong> leur prochain prélèvement.
                </p>
                <div className="bg-[#FAFAFA] rounded-xl p-4">
                  <h4 className="font-medium text-[#1A252C] mb-2">Le courriel de rappel inclut :</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Le montant et la date du prochain prélèvement</li>
                    <li>Un lien pour gérer l'abonnement</li>
                    <li>Un message de remerciement personnalisé</li>
                  </ul>
                </div>
                <p className="text-sm">
                  Cliquez sur <strong>"Envoyer les rappels"</strong> pour traiter manuellement 
                  tous les rappels en attente. Le système évite automatiquement les envois en double.
                </p>
              </div>
            </div>

            {/* Anniversary Section */}
            <div className="mt-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-serif text-2xl font-bold text-[#1A252C]">
                    🎉 Anniversaires de fidélité
                  </h2>
                  <p className="text-[#546A76] mt-1">
                    Célébrez vos donateurs fidèles avec des courriels de remerciement personnalisés
                  </p>
                </div>
                <Button
                  onClick={handleSendAnniversaries}
                  disabled={sendingAnniversaries}
                  className="primary-btn rounded-full"
                  data-testid="send-anniversaries-btn"
                >
                  {sendingAnniversaries ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4 mr-2" />
                      Envoyer les anniversaires
                    </>
                  )}
                </Button>
              </div>

              {/* Anniversary Stats Cards */}
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1A252C]">
                    {anniversaryStats?.upcoming_7_days || 0}
                  </p>
                  <p className="text-sm text-[#546A76]">Anniversaires dans 7 jours</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1A252C]">
                    {anniversaryStats?.loyal_donors_1yr_plus || 0}
                  </p>
                  <p className="text-sm text-[#546A76]">Donateurs fidèles (1+ an)</p>
                </div>
                
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1A252C]">
                    {anniversaryStats?.sent_this_month || 0}
                  </p>
                  <p className="text-sm text-[#546A76]">Envoyés ce mois</p>
                </div>
              </div>

              {/* Upcoming Anniversaries List */}
              {anniversaryStats?.upcoming_details && anniversaryStats.upcoming_details.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                  <h3 className="font-serif text-lg font-semibold text-[#1A252C] mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#4A708B]" />
                    Prochains anniversaires
                  </h3>
                  <div className="space-y-3">
                    {anniversaryStats.upcoming_details.map((donor, idx) => (
                      <div key={`anniversary-${idx}`} className="flex items-center justify-between p-4 rounded-xl bg-[#FAFAFA]">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B5952F] flex items-center justify-center text-white font-bold">
                            {donor.name?.charAt(0)?.toUpperCase() || 'D'}
                          </div>
                          <div>
                            <p className="font-medium text-[#1A252C]">{donor.name || donor.email}</p>
                            <p className="text-sm text-[#546A76]">{donor.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#D4AF37]">{donor.years} an{donor.years > 1 ? 's' : ''}</p>
                          <p className="text-sm text-[#546A76]">{donor.anniversary_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Information Box */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-serif text-lg font-semibold text-[#1A252C] mb-4">
                  À propos des courriels d'anniversaire
                </h3>
                <div className="space-y-4 text-[#546A76]">
                  <p>
                    Chaque année, vos donateurs fidèles reçoivent un courriel personnalisé 
                    pour les remercier de leur engagement continu envers la Fondation.
                  </p>
                  <div className="bg-[#FAFAFA] rounded-xl p-4">
                    <h4 className="font-medium text-[#1A252C] mb-2">Le courriel d'anniversaire inclut :</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Le nombre d'années de fidélité</li>
                      <li>Le total des dons effectués</li>
                      <li>Un message de gratitude personnalisé</li>
                      <li>Un récapitulatif de l'impact de leur générosité</li>
                    </ul>
                  </div>
                  <p className="text-sm">
                    Le système envoie automatiquement un courriel à la date anniversaire 
                    de l'inscription du donateur (un seul envoi par an).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
